<template>
  <div class="building-detail-container">
    <div>
      <a
        class="nav-back"
        @click="$router.go(-1)"
      >
        {{ $t('back') }}
      </a>
    </div>

    <hr>
    <div class="header">
      <label class="title">{{ $t('settings.tab.buildingDetail') }}</label>
      <div
        v-if="aclHelper.getSettingsUpdateAccess()"
        class="row controls"
      >
        <button
          v-if="editView && !isCreatingBuilding"
          v-hkldreset
          type="button"
          class="btn btn--darkblue"
          @click="discardChange()"
        >
          {{ $t('action.cancel') }}
        </button>
        <button
          v-if="!editView"
          type="button"
          class="btn btn--orange"
          @click="toEditMode()"
        >
          {{ $t('action.edit') }}
        </button>
        <button
          v-if="editView"
          v-hkldreset
          type="button"
          class="btn btn--orange"
          @click="save()"
        >
          {{ $t('action.save') }}
        </button>
      </div>
    </div>

    <div class="main">
      <div class="tabs">
        <label
          v-for="header in itemHeaders"
          :key="header.id"
          :class="{active: header.id === buildingId}"
          @click="navToDetail(header.id)"
        >
          {{ header.name }}
        </label>
      </div>
    </div>
    <div class="profile-content-container">
      <div class="building-label clearfix u-sec-margin-bottom">
        <label
          class="emphasis"
          :class="{ 'required-flag': editView }"
        >
          {{ $t('settings.building.buildingName') }}:
        </label>
        <label>{{ buildingDetail.code || $t('emptyValue') }}</label>
        <label
          class="emphasis"
          :class="{ 'required-flag': editView }"
        >
          {{ $t('settings.building.buildingDesc') }}:
        </label>
        <input
          v-if="editView"
          id="modules-settings-building-detail-fullname"
          v-model="buildingDetail.fullName"
          v-hkldinput
          type="text"
          class="building-detail-container-input"
        >
        <label v-else>{{ buildingDetail.fullName || $t('emptyValue') }}</label>
        <hr>
        <div>
          <label
            class="emphasis"
            :class="{ 'required-flag': editView }"
          >
            {{ $t('settings.building.zones') }}
          </label>
          <div v-if="editView">
            <div
              v-for="(item, index) in zones"
              :key="index"
              class="building-zones"
            >
              <InputField
                class="small-2 mediu-2 large2"
                :showFlag="false"
                :showColon="false"
                :value="item"
                extendClass="zone-input"
                @updateValue="val=>updateZone(val, index)"
              />
              <div
                v-if="editView"
                class="list__item--delete"
                @click="deleteZone(index)"
              >
                <i class="fa fa-times fa-lg" />
              </div>
            </div>
            <i
              class="building-info__contacts--add"
              @click="addZones()"
            >
              {{ $t('settings.building.addNewZone') }} +
            </i>
          </div>
          <label v-else>{{ buildingZones || $t('emptyValue') }}</label>
        </div>

        <div>
          <label
            class="emphasis"
            :class="{ 'required-flag': editView }"
          >
            {{ $t('settings.building.floors') }}
          </label>
          <div
            v-if="editView"
            class="floors-input-box"
          >
            <div>{{ finalFloorsList.join(', ') || $t('emptyValue') }}</div>
            <br>
            <textarea
              v-if="editView"
              id="modules-settings-building-detail-floors"
              v-model="inputFloorsString"
              v-autosize
              v-hkldinput
              :placeholder="`${$t('abbrivations.example')} 01F~12F, MF, 14F~100F`"
              @input="onInputFloors()"
            />
          </div>
          <label v-else>{{ floorsList || $t('emptyValue') }}</label>
        </div>

        <div>
          <label class="emphasis">{{ $t('settings.building.commonFloors') }}</label>
          <div
            v-if="editView"
            class="common-floor-box"
          >
            <HKLMultiSelect
              class="multi-value"
              :options="finalFloorsList"
              :value="sortedSelectedCommonFloors"
              :showSearch="true"
              placeholder=""
              @change="val => { selectedCommonFloors = val }"
            />
          </div>
          <label v-else>{{ commonFloorsStr || $t('emptyValue') }}</label>
        </div>

        <label
          v-if="enableTenantProjectsFeature"
          class="emphasis"
        >
          {{ $t('settings.building.handoverTpl') }}
        </label>
        <div
          v-if="enableTenantProjectsFeature"
          class="template-list"
        >
          <table>
            <thead>
              <tr>
                <th class="template-name">
                  {{ $t('settings.template.templateName') }}
                </th>
                <th class="template-updated-by">
                  {{ $t('handover.lastUpdateOnandBy') }}
                </th>
              </tr>
            </thead>
            <tbody class="handover-template-table">
              <tr
                v-for="(temp, index) in templateList"
                :key="index"
              >
                <td
                  class="template-name"
                  @click="gotoEditTemplatePage(temp.id)"
                >
                  <a>{{ temp.title }}</a>
                </td>
                <td class="template-updated-by">
                  <span>{{ temp.updator.displayName + ' ' + formatDate(temp.updatedAt) }}</span>
                  <i
                    v-if="templateList.length > 1 && isDeleteBtnShow"
                    class="operate fa-times fa"
                    @click="openModal(temp, index)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
          <button
            type="button"
            class="btn btn--darkblue"
            :disabled="isCreatingBuilding"
            @click="gotoEditTemplatePage()"
          >
            {{ $t('settings.building.addTemplate') }} +
          </button>
        </div>
        <template v-if="enableGreenOperationFeature">
          <hr>
          <BuildingComfyInstallation v-model="buildingDetail.comfyInstallation" />
        </template>
        <Modal
          ref="confirm-delete-template-modal"
          class="confirm-delete-template-modal"
          :modalId="`delete-template-${+new Date()}`"
        >
          <p
            slot="modal-header"
            class="font-bold"
          >
            {{ $t('settings.template.deleteHint') }}
          </p>
          <div slot="btns">
            <button
              class="btn--orange"
              @click="closeModal"
            >
              {{ $t('action.cancel') }}
            </button>
            <button
              class="btn--darkblue"
              @click="delOneTemplate"
            >
              {{ $t('action.ok') }}
            </button>
          </div>
        </Modal>
      </div>

      <div class="float-right building-user">
        <div>
          <label
            v-for="(building, index) in buildingDetail.buildings"
            :key="index"
          >
            <span
              class="building_name--link"
              @click="navTo('building-handover-templates', building.id)"
            >{{ building.code }}: {{ building.fullName }}</span>
          </label>
        </div>

        <div class="users">
          <table>
            <thead>
              <tr>
                <th>{{ $t('settings.buildingUser') }}</th>
                <th>{{ $t('status.name') }}</th>
                <th>{{ $t('lastLogin') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <input
                    v-if="items.length > 0"
                    v-model="filterUser"
                    class="table-input"
                    type="text"
                  >
                </td>
                <td class="settings-filter">
                  <HKLSelect
                    :value="filterStatus"
                    class="table-multiselect align-center"
                    settings-filter-row
                    :options="statusHklOptions"
                    :showSearch="false"
                    placeholder=""
                    @change="val => { filterStatus = val }"
                  />
                </td>
                <td />
              </tr>
              <tr
                v-for="(item, index) in orderedItems"
                :key="index"
              >
                <td>
                  <span>{{ userName(item) }}</span>
                  <i
                    v-if="!item.enabled"
                    class="fa fa-ban"
                    aria-hidden="true"
                  />
                </td>
                <td>
                  <span>{{ showStatus(item) | customStatusLabel }}</span>
                </td>
                <td>
                  <span>{{ lastLoginAt(item) }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import { createBuilding, delTemplatesFromBuilding, getBuildings, getBuildingsWithUsers, updateBuilding } from '@/api/buildings/request'
import { ROUTER_PATHS } from '@/constants/router'
import InputField from '@/controls/InputField'
import Modal from '@/controls/Modal'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import HKLMultiSelect from '@/controls/Select/HKLMultiSelect.vue'
import { useRouteQuery } from '@/router/useRouter'
import { aclHelper } from '@/utils/aclHelper'
import cookie from '@/utils/cookie'
import dateUtil from '@/utils/dateUtil'
import { userStatusAllOptions, userStatusMapping } from '@/utils/locale'
import navigation from '@/utils/navigation'
import notification from '@/utils/notification'
import userHelper from '@/utils/userHelper'
import BuildingComfyInstallation from './BuildingComfyInstallation.vue'
import { useStoreGetters } from '@/store/index'

function getDefaultData () {
  const options = userStatusAllOptions
  const windowWidth = $(window).width() - 80
  const maxCols = (windowWidth > 1024) ? Math.floor(windowWidth / 175) : 5
  const headerPageSize = cookie.get('settingColumnSize') || maxCols
  return {
    buildingDetail: {},
    items: [],
    filterUser: '',
    options,
    filterStatus: options[0],
    pendingUsers: {},
    aclHelper,
    validateHighlight: false,
    templateList: [],
    buildingId: '',
    deleteIndex: -1,
    deleteItem: '',
    toDelTemplateList: [],
    headerPageSize,
    headerPageNum: 1,
    itemHeaders: [],
    orderbyAttr: 'updateDate',
    inputFloorsString: '',
    selectedCommonFloors: [],
  }
}

export default {
  components: {
    BuildingComfyInstallation,
    HKLSelect,
    HKLMultiSelect,
    InputField,
    Modal,
  },
  beforeRouteUpdate: async function (to, from, next) {
    await this.initPage(to.params.id)
    next()
  },
  setup () {
    const { enableGreenOperationFeature, enableTenantProjectsFeature } = useStoreGetters('regions', ['enableGreenOperationFeature', 'enableTenantProjectsFeature'])

    const editView = useRouteQuery('isEdit')
    return {
      editView,
      enableTenantProjectsFeature,
      enableGreenOperationFeature,
    }
  },
  data: getDefaultData,
  computed: {
    ...mapGetters('user', ['currentUser']),
    orderedItems () {
      const filteredResult =
        _.filter(this.items, item => (
          (item.username.toString().split('@')[0].toLowerCase().indexOf(this.filterUser.toLowerCase()) !== -1 ||
          (item.displayName || ' ').toString().toLowerCase().indexOf(this.filterUser.toLowerCase()) !== -1) &&
          (!this.filterStatus ||
            this.filterStatus === this.options[0] ||
            (this.filterStatus === userStatusMapping[item.enabled]))
        ))
      const result = _.orderBy(filteredResult, ['displayName', 'enabled', 'lastLoginAt'])
      return this.reverse ? result.reverse() : result
    },
    maxHeaderPageNum () {
      return Math.max(1, Math.ceil((this.itemHeaders.length - 1) / (this.headerPageSize)))
    },
    buildingInfo () {
      return this.buildingDetail
    },
    buildingZones () {
      return this.buildingDetail.zones ? this.buildingDetail.zones.join(', ') : ''
    },
    isDeleteBtnShow () {
      return aclHelper.getSettingsUpdateAccess() && this.editView
    },
    isCreatingBuilding () {
      return this.$router.currentRoute.params.id === 'createBuilding'
    },
    zones () {
      return this.buildingInfo.zones
    },
    floorsList () {
      return this.buildingDetail.floor ? this.buildingDetail.floor.join(', ') : ''
    },
    finalFloorsList () {
      return this.getFormattedFloorsList(this.inputFloorsString) || []
    },
    commonFloorsStr () {
      return this.buildingDetail.commonFloor ? this.buildingDetail.commonFloor.join(', ') : ''
    },
    sortedSelectedCommonFloors () {
      // remove common floors that are removed in floors
      const filteredSelectedCommonFloors = this.selectedCommonFloors.filter(item => this.finalFloorsList.includes(item))

      // sort as floors
      return filteredSelectedCommonFloors.sort((a, b) => {
        return this.finalFloorsList.indexOf(a) - this.finalFloorsList.indexOf(b)
      })
    },
    statusHklOptions () {
      return this.options.map((value) => ({
        name: this.$options.filters.customStatusLabel(value),
        value,
      }))
    },
  },
  async mounted () {
    if (this.$router.currentRoute.params.id === 'createBuilding') {
      this.toEditMode()
      this.buildingDetail = {fullName: '', code: '', zones: []}
      this.items = []
    } else {
      await this.initPage()
    }
  },
  beforeDestroy: function () {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    ...mapActions('user', ['reloadUser']),
    ...mapActions('buildings', ['getAllBuildings']),

    showStatus (user) {
      return userStatusMapping[user.enabled]
    },
    userName (user) {
      if (user.username) {
        const name = userHelper.getUserName(user.username, user.displayName)
        return name
      }
    },
    lastLoginAt (user) {
      return this.formatDate(user.lastLoginAt)
    },
    formatDate (item) {
      return dateUtil.formatDate(item, true)
    },

    gotoEditTemplatePage (tid = 'createTemplate') {
      const params = {
        id: this.buildingId,
        tid: tid}
      this.navTo('building-detail', params)
    },
    async navToDetail (id) {
      if (id) {
        this.$router.replace({name: ROUTER_PATHS.BUILDING_PROFILE, params: {id}})
      }
    },
    async delOneTemplate () {
      this.toDelTemplateList.push(this.deleteItem.id)
      this.templateList.splice(this.deleteIndex, 1)
      this.closeModal()
    },
    openModal (item, index) {
      this.deleteIndex = index
      this.deleteItem = item
      this.$refs['confirm-delete-template-modal'].open()
    },
    closeModal () {
      this.$refs['confirm-delete-template-modal'].close()
    },

    reset (keep) {
      const def = getDefaultData()
      def[keep] = this[keep]
      Object.assign(this.$data, def)
    },
    discardChange () {
      this.toPreviewMode()
    },
    async initPage (id) {
      this.buildingId = id || this.$router.currentRoute.params.id
      const response = await getBuildingsWithUsers(this.buildingId)
      if (response) {
        this.buildingDetail = { ...response.building }
        this.items = response.users
        this.templateList = this.buildingDetail.templates
        this.inputFloorsString = this.buildingDetail.floor.join(', ')
        this.selectedCommonFloors = this.buildingDetail.commonFloor
      }
      const buildings = await getBuildings()
      const orderedBuildings = _.orderBy(buildings, 'code')
      this.initHeader(orderedBuildings)
      window.addEventListener('resize', this.handleResize)
      const tableWidth = document.getElementsByClassName('building-label').offsetWidth
      this.maxCols = (tableWidth > 1024) ? Math.floor((tableWidth - 270) / 132) : 8
      this.headerPageSize = cookie.get('settingColumnSize') || this.maxCols
    },
    handleResize () {
      const tableWidth = document.getElementsByClassName('building-label').offsetWidth
      this.maxCols = (tableWidth > 1024) ? Math.floor((tableWidth - 270) / 132) : 5
      this.headerPageSize = cookie.get('settingColumnSize') || this.maxCols
    },
    initHeader (buildings) {
      this.itemHeaders = buildings.map((building) => ({
        code: building.code,
        name: building.code,
        id: building.id,
      }))
    },
    toPreviewMode () {
      this.$router.replace({
        name: ROUTER_PATHS.BUILDING_PROFILE,
        params: { id: this.$router.currentRoute.params.id },
      })
    },
    toEditMode () {
      this.$router.replace({
        name: ROUTER_PATHS.BUILDING_PROFILE,
        params: { id: this.$router.currentRoute.params.id },
        query: { isEdit: true },
      })
    },
    async save () {
      this.validateHighlight = true
      let zoneHighlight = false
      const lowercaseZones = this.buildingDetail.zones.map(zone => zone.toLowerCase())
      const uniqZonesValue = new Set(lowercaseZones)
      if (uniqZonesValue.size > 0 && uniqZonesValue.size !== this.buildingDetail.zones.length) {
        zoneHighlight = true
        notification.fireNotification(false, `${this.$i18n.t('error.duplicateValue', [this.$i18n.t('settings.building.zones')])}`)
      }
      if (zoneHighlight) {
        return false
      }
      const { code, fullName } = this.buildingDetail
      if (code.trim().length === 0 || fullName.trim().length === 0 || this.inputFloorsString.trim().length === 0 || this.zones.length === 0) {
        notification.fireNotification(false, `${this.$i18n.t('error.checkInput')}`)
        return
      }
      let initWithId
      if (!this.buildingDetail.id) {
        const response = await createBuilding(this.buildingDetail)
        const id = response.id
        this.$router.replace({name: ROUTER_PATHS.BUILDING_PROFILE, params: {id}})
        initWithId = id
      } else {
        this.buildingDetail.floor = this.finalFloorsList
        this.buildingDetail.commonFloor = this.sortedSelectedCommonFloors
        await updateBuilding(this.buildingDetail.id, this.buildingDetail)
        if (this.toDelTemplateList.length) {
          await delTemplatesFromBuilding(this.buildingId, this.toDelTemplateList)
        }
      }

      // To update the latest buildings for Vuex
      await this.getAllBuildings()

      this.reset()
      await this.reloadUser({ adId: this.currentUser.AD_id })
      this.toPreviewMode()
      await this.initPage(initWithId)
    },
    navTo (routeName, params) {
      navigation.toPage(routeName, params)
    },
    addZones () {
      this.buildingDetail.zones.push(null)
    },
    deleteZone (index) {
      this.buildingDetail.zones.splice(index, 1)
    },
    updateZone (val, index) {
      this.buildingDetail.zones[index] = val
    },

    getFormattedFloorsList (inputFloorsString) {
      // remove repeated elements and spaces
      const inputFloorsList = inputFloorsString.replace(/\s+/g, '').toUpperCase().split(',')
      const uniqInputFloorsList = [...new Set(inputFloorsList)].filter(item => item && item.trim())
      const formattedFloorsList = uniqInputFloorsList.map(item => item.indexOf('~') > -1 ? this.destructureFloor(item) : item).flat()
      return formattedFloorsList
    },

    // simpleFloor sample: 09F~11F  ->  09F, 10F, 11F
    destructureFloor (simpleFloor) {
      const startEndFloor = simpleFloor.split('~') // ["09F", "11F"]
      const targetFloorList = []
      for (let i = Number(startEndFloor[0].slice(0, -1)); i <= Number(startEndFloor[1].slice(0, -1)); i++) {
        targetFloorList.push(i + 'F')
      }
      return targetFloorList.map(item => item.padStart(3, 0))
    },

    onInputFloors () {
      this.inputFloorsString = this.inputFloorsString.replace(/[^\w,~\s]/ig, '')
    },

  },
}
</script>

<style lang="scss">
@import "~styles/newMultiselect";
</style>

<style lang="scss" scoped>
@import '~styles/settings/style';
.profile-content-container {
  display: flex;
  justify-content: space-between;
  padding: 5px;
  background: #f8f8f8;
  .building-label {
    flex: 3;
    background: white;
    margin-right: 15px;
    padding-bottom: 15px;
  }
  .building-user {
    flex: 2;
    background: white;
    padding: 15px;
  }
}
.building-detail-container {
  padding-left: 24px;
  padding-right: 24px;
  .u-margin-top {
    margin-top: 10px;
  }
  .u-sec-margin-bottom {
    margin-bottom: 25px;
  }
  .building-detail-container-input {
    width: 50%;
    min-width: 500px;
  }

  .building-label, .buildings-label {
    margin-bottom: 20px;
  }

  ul {
    display: flex;
    justify-content: center;
  }
  .align-center {
    margin: auto
  }
  input {
    margin-bottom: 10px;
  }

  .building-label {
    padding: 0 24px;
  }
  .nav-back {
    padding-left: 8px;
  }
  .building_name--link {
    text-decoration: underline;
    cursor: pointer;
    cursor: hand;
  }

  .building-zones {
    display: flex;
    align-items: center;

  }
  .zone-input {
    outline: none;
  }

}

.building-detail-container .building-list {
  list-style: none;
  display: block;
}

.building-info {
  background-color: #FAFBFC;
  margin-bottom: 20px;
  &__contacts {
    margin-top: 15px;
    &--label {
      width: 188px;
      height: 20px;
      font-family: Verdana;
      font-size: 14px;
      font-weight: bold;
      line-height: 1.43;
      color: #0c3a58;
    }
  }
  &__contacts--title {
    max-width: 808px;
    margin-top: 15px;
    padding: 0 20px;
    height: 40px;
    background-color: #ffffff;
    border: solid 1px #ebebeb;
    display: flex;
    justify-content: space-between;
    align-items: center;
    .title__name, .title__role {
      height: 20px;
      font-size: 14px;
      font-weight: bold;
      color: #083e62;
    }
    .title__role {
      width: 59%;
    }
  }
  &__contacts--list {
    list-style: none;
    margin-left: 0;
    max-width: 808px;
    border: solid 1px #ebebeb;
    background-color: #f8f8f8;
    padding: 0 15px;
    .list__item {
      margin: 5px 0;
      padding-left: 5px;
      padding-right: 20px;
      height: 40px;
      display: flex;
      align-items: center;

      &--name {
        width: 32%;
        margin-right: 10%;
      }
      &--role {
        width: 45%;
        input {
          margin-bottom: 0;
        }
      }
      &--delete {
        font-size: 18px;
        color: #9B9B9B;
        margin-left: 80px;
      }
    }
  }
  &__contacts--add {
    width: 270px;
    height: 18px;
    font-size: 15px;
    font-style: italic;
    text-align: left;
    color: #0c3a58;
    text-decoration: underline;
    cursor: pointer;
  }
}

  .template-name {
    width: 60%;
  }
  .template-updated-by {
    width: 40%;
  }
  .template-list {
    margin-top: 20px;
    margin-bottom: 20px;
    overflow: auto;
    th {
      text-transform: uppercase;
      color: #0C3A58;
    }
    tr{
      background-color: #FAFBFC;
    }
    .operate {
      float: right;
      font-size: 20px;
      cursor: pointer;
    }
  }

.controls {
  float: initial;
  display: flex;
  flex-direction: row-reverse;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 20px;
  .title {
    display: inline-block;
    font-family: Georgia;
    font-size: 18px;
    font-weight: bold;
    color: #0C3A58;
  }
}

.handover-template-table{
  tr:first-child{
    background-color: #fff;
  }
  tr:nth-child(even) {
    background-color: #fff;
  }
  tr:nth-child(odd) {
    background-color: #f1f1f1;
  }
}

.main {
  padding: 5px;
  background: #f8f8f8;
  .tabs {
    display: block;
    margin: 10px 0 15px 0;
    background: transparent;
    border: 0;
    label {
      display: inline-block;
      background: white;
      margin-right: 5px;
      font-weight: bold;
      padding: 5px 20px;
      cursor: pointer;
      &.active {
        background: #0C3A58;
        color: white;
      }
    }
  }
}

.table-input, .table-multiselect {
  min-width: 100% !important;
}
</style>
